import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import eyes from "../asset/intrests/eyes.png";

const faqs = [
  {
    question: "How do you usually start a project?",
    answer:
      "I start by understanding your goals, content, and what you’re trying to achieve. Once we’re aligned, I outline the structure, scope, and timeline before development begins.",
  },
  {
    question: "Do you design or only develop?",
    answer:
      "I focus on development. If you already have a design or template, I’ll build from that. If not, I can help you choose a solid template or connect you with a UI/UX designer.",
  },
  {
    question: "What’s the typical timeline?",
    answer:
      "Landing pages usually take 24–48 hours. Standard websites take 1–2 weeks, depending on complexity and features.",
  },
  {
    question: "How do you balance design and functionality?",
    answer:
      "Good design is how something works, not just how it looks. I aim for interfaces that feel intuitive, intentional, and visually polished without sacrificing performance or usability.",
  },
  {
    question: "How do you handle feedback or changes?",
    answer:
      "I expect feedback and welcome iteration. Changes are part of building good products, and I adapt quickly while keeping the core goals and user experience intact.",
  },
  {
    question: "Do you offer post-launch support?",
    answer:
      "Yes. I include a support period of ten (10) days after launch to fix any bugs or make minor adjustments. Ongoing maintenance is also available.",
  },
  {
    question: "How do you charge?",
    answer:
      "Pricing depends on scope and complexity. I offer fixed pricing and hourly rates. A 50% deposit is required to begin, with the balance due on completion.",
  },
  {
    question: "Why should I trust you to deliver my vision?",
    answer:
      "I focus on clarity, communication, and alignment. I share progress, adapt to feedback, and deliver exactly what we agreed on. No guesswork.",
  },
];

const Faq = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const toggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="relative border-[#7C6AE6] border-b-2">
      <section className="relative py-[55px] md:py-[90px]  overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 w-full">

          {/* Heading */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h2 className="text-[26px] md:text-[36px] font-medium uppercase text-white">
                Quick answers
              </h2>
              <p className="mt-3 text-base md:text-lg text-white">
                Around timelines, process, and working together.
              </p>
            </motion.div>

            {/* Eyes image  */}
            <motion.img
              src={eyes}
              alt=""
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 0.25, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="
                w-[42px]
                hidden
                md:flex
                md:w-[70px]
                opacity-25
                select-none
                pointer-events-none
              "
            />
          </div>

          {/* FAQ Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {faqs.map((faq, index) => {
              const isOpen = activeIndex === index;

              return (
                <motion.div
                  key={index}
                  layout
                  transition={{
                    layout: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
                  }}
                  className="relative w-full bg-[#7C6AE6] rounded-[16px] px-6 py-6"
                >
                  {/* Question */}
                  <motion.button
                    whileTap={{ scale: 0.985 }}
                    onClick={() => toggle(index)}
                    className="w-full flex items-center justify-between text-left font-semibold uppercase text-white"
                  >
                    <motion.span
                      animate={{ opacity: isOpen ? 1 : 0.85 }}
                      className="text-[15px] md:text-base"
                    >
                      {index + 1}. {faq.question}
                    </motion.span>

                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="ml-4 flex h-8 w-8 items-center justify-center text-lg font-bold"
                    >
                      {isOpen ? "−" : "+"}
                    </motion.span>
                  </motion.button>

                  {/* Answer */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.4,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="overflow-hidden"
                      >
                        <motion.p
                          initial={{ y: -4 }}
                          animate={{ y: 0 }}
                          exit={{ y: -4 }}
                          transition={{ duration: 0.25 }}
                          className="mt-4 text-white uppercase leading-relaxed text-[15px]"
                        >
                          {faq.answer}
                        </motion.p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Speech bubble tail */}
                  <motion.div
                    animate={{ opacity: isOpen ? 1 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="absolute bottom-[-10px] left-10 h-5 w-5 bg-[#7C6AE6] rotate-45"
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Faq;
